import type { PageLink, PageSocialLink } from "../api/pages";
import { buildWhatsappLink } from "../lib/whatsapp";

export interface PageViewData {
  slug: string;
  title?: string | null;
  links_label?: string | null;
  show_links_label?: boolean | null;
  title_size?: "small" | "medium" | "large" | null;
  logo_base64?: string | null;
  description?: string | null;
  background_color?: string | null;
  instagram_url?: string | null;
  whatsapp_phone?: string | null;
  whatsapp_message?: string | null;
  links?: PageLink[];
  social_links?: PageSocialLink[];
}

function resolveLinkLabel(link: PageLink) {
  if (link.label) return link.label;
  switch (link.type) {
    case "instagram":
      return "Instagram";
    case "facebook":
      return "Facebook";
    case "whatsapp":
      return "WhatsApp";
    default:
      return "Link";
  }
}

function buildCustomLinks(links?: PageLink[]) {
  if (!links || links.length === 0) return [];

  return (
    links
      .map(link => {
        const href =
          link.type === "whatsapp"
            ? (link.whatsapp_phone ? buildWhatsappLink(link.whatsapp_phone, link.whatsapp_message || undefined) : "")
            : link.url || "";
        if (!href) return null;
        return { ...link, href };
      })
      .filter((link): link is PageLink & { href: string } => Boolean(link)) || []
  );
}

function getSocialIcon(network: string) {
  switch (network.toLowerCase()) {
    case 'instagram':
      return '📸';
    case 'facebook':
      return '📘';
    case 'tiktok':
      return '🎵';
    case 'youtube':
      return '📺';
    case 'twitter':
    case 'x':
      return '🐦';
    case 'linkedin':
      return '💼';
    default:
      return '🔗';
  }
}

function getSocialLabel(network: string) {
  switch (network.toLowerCase()) {
    case 'instagram':
      return 'Instagram';
    case 'facebook':
      return 'Facebook';
    case 'tiktok':
      return 'TikTok';
    case 'youtube':
      return 'YouTube';
    case 'twitter':
    case 'x':
      return 'Twitter / X';
    case 'linkedin':
      return 'LinkedIn';
    default:
      return network;
  }
}

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16)
  } : null;
}

function getLuminance(r: number, g: number, b: number) {
  const rs = r / 255 <= 0.03928 ? r / 255 / 12.92 : Math.pow((r / 255 + 0.055) / 1.055, 2.4);
  const gs = g / 255 <= 0.03928 ? g / 255 / 12.92 : Math.pow((g / 255 + 0.055) / 1.055, 2.4);
  const bs = b / 255 <= 0.03928 ? b / 255 / 12.92 : Math.pow((b / 255 + 0.055) / 1.055, 2.4);
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getContrastColor(backgroundColor: string): 'white' | 'black' {
  // Default to light gray if no background color
  if (!backgroundColor || backgroundColor === '#f0f0f0') {
    return 'black';
  }

  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return 'black';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  // Use white text on dark backgrounds, black text on light backgrounds
  return luminance < 0.5 ? 'white' : 'black';
}

export function PageView({
  page,
  showCta = false,
  hasActiveSubscription = true,
}: {
  page: PageViewData;
  showCta?: boolean;
  hasActiveSubscription?: boolean;
}) {
  const whatsappLink = page.whatsapp_phone ? buildWhatsappLink(page.whatsapp_phone, page.whatsapp_message || undefined) : null;
  const customLinks = buildCustomLinks(page.links);
  const backgroundColor = page.background_color || '#ffffff'; // Changed default to white to match design
  const textColor = getContrastColor(backgroundColor);
  const isDarkText = textColor === 'black';

  // Common button style matching the design
  const buttonClassName = "w-full flex items-center justify-between rounded-2xl bg-white border border-neutral-200 text-neutral-900 font-medium px-6 py-4 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5";

  // Create unified list: custom links first, then social networks
  const allButtons = [
    // Instagram (if configured)
    ...(page.instagram_url ? [{
      id: 'instagram',
      href: page.instagram_url.startsWith("http")
        ? page.instagram_url
        : `https://instagram.com/${page.instagram_url.replace(/^@/, "")}`,
      label: 'Instagram',
      icon: '📸',
      className: buttonClassName
    }] : []),
    // WhatsApp (if configured)
    ...(whatsappLink ? [{
      id: 'whatsapp',
      href: whatsappLink,
      label: 'WhatsApp',
      icon: '💬',
      className: buttonClassName
    }] : []),
    // Custom links
    ...customLinks.map(link => ({
      id: link.id,
      href: link.href,
      label: resolveLinkLabel(link),
      icon: '🔗', // You might want to map specific icons based on link type if available
      className: buttonClassName
    })),
    // Social networks
    ...(page.social_links || []).map(social => ({
      id: social.id,
      href: social.url,
      label: getSocialLabel(social.network),
      icon: getSocialIcon(social.network),
      className: buttonClassName
    }))
  ];

  return (
    <div
      className="min-h-screen flex flex-col items-center px-6 pt-12 pb-16 transition-colors duration-300 font-sans overflow-hidden"
      style={{ backgroundColor, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      onScroll={(e) => e.currentTarget.style.overflow = 'hidden'}
    >
      <style dangerouslySetInnerHTML={{
        __html: `
          div::-webkit-scrollbar {
            display: none;
          }
        ` as string
      }} />
      <div className={`max-w-md w-full text-center ${page.logo_base64 ? 'space-y-4' : 'space-y-6'}`}>
        {/* Logo */}
        {page.logo_base64 && (
          <div className="flex justify-center mb-2">
            <div className="p-1 bg-white rounded-full shadow-sm">
              <img
                src={page.logo_base64}
                alt={`Logo de ${page.title || page.slug}`}
                className="w-32 h-32 rounded-full object-cover"
              />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="space-y-1">
          <h1 className={`text-2xl font-bold leading-tight ${isDarkText ? 'text-neutral-900' : 'text-white'}`}>
            {page.title || `/${page.slug}`}
          </h1>
        </div>

        {/* Description */}
        <div className="py-2">
          <p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDarkText ? 'text-neutral-600' : 'text-white/90'}`}>
            {page.description ||
              "Esta pessoa ainda não escreveu uma descrição, mas já reservou o endereço dela por aqui."}
          </p>
        </div>

        {/* Links Header */}
        {allButtons.length > 0 && page.show_links_label !== false && (page.show_links_label as any) !== 0 && (
          <div className="pt-4 pb-2">
            <h2 className={`text-xl font-bold ${isDarkText ? 'text-neutral-900' : 'text-white'}`}>
              {page.links_label || "Meus links"}
            </h2>
          </div>
        )}

        {/* Unified Button List */}
        {allButtons.length > 0 && (
          <div className="flex flex-col gap-3">
            {allButtons.map(button => (
              <a
                key={button.id}
                href={button.href}
                target="_blank"
                rel="noreferrer"
                className={button.className}
              >
                <span className="text-xl w-8 text-left">{button.icon}</span>
                <span className="flex-1 text-center">{button.label}</span>
                <span className="w-8"></span> {/* Spacer for centering */}
              </a>
            ))}
          </div>
        )}

        {/* CTA */}
        {showCta && (
          <div className="pt-12 text-center">
            <p className={`${isDarkText ? 'text-neutral-400' : 'text-white/60'} text-xs`}>
              Feito com{" "}
              <a
                href="/register"
                className="font-medium hover:underline"
              >
                Link na Bio
              </a>
            </p>
          </div>
        )}

        {/* Branding Footer - shown when user has no active subscription */}
        {!hasActiveSubscription && !showCta && (
          <div className="pt-12 text-center">
            <p className={`${isDarkText ? 'text-neutral-400' : 'text-white/60'} text-xs`}>
              criado com{" "}
              <a
                href="https://hublinks.click"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
              >
                HubLinks.click
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
